// src/utils/excluded-properties.js

/**
 * Categorized properties to exclude from baseline comparisons.
 * These are typically metadata fields that change frequently or are 
 * environment-specific and don't affect functional configuration.
 */

export const EXCLUDED_PROPERTY_CATEGORIES = {
    // Metadata and versioning
    metadata: [
        "CreatedBy",
        "LastModifiedBy",
        "createdDateTime",
        "lastModifiedDateTime",
        "CreatedDateTime",
        "LastModifiedDateTime",
        "version",
        "Priority",
        "ReleaseTrack",
        "AdminDisplayVersion",
        "RBACConfigurationVersion",
    ],

    // Unique identifiers
    identifiers: [
        "id",
        "Guid",
        "Identity",
        "ImmutableId",
        "ExchangeObjectId",
        "securityIdentifier",
        "Name",
    ],

    // System-generated properties
    systemGenerated: [
        "OrganizationId",
        "OrganizationalUnitRoot",
        "ObjectCategory",
        "DistinguishedName",
        "UnifiedAuditLogFirstOptInDate",
    ],

    // Regional and location settings
    regionalSettings: [
        "DefaultMailboxRegion",
        "AllowedMailboxRegions",
        "CurrentMailboxForests",
    ],

    // Authentication and MFA tokens
    authenticationTokens: [
        "accountId",
        "tokenCreationDateTime",
        "qrCodeContent",
        "qrCodeImage",
        "tokenValue",
    ],

    // Exchange-specific properties
    exchangeSpecific: [
        "RejectAnonymousDirectSend",
        "RPSEnabled",
        "MicrosoftExchangeRecipientEmailAddresses",
        "MicrosoftExchangeRecipientPrimarySmtpAddress",
        "LegacyExchangeDN",
        "InPlaceHolds",
    ],

    // OData and context metadata
    odataMetadata: [
        "@odata.type",
        "@odata.context",
        "authenticationStrength@odata.context",
        "grantControls.authenticationStrength@odata.context",
        "settings@odata.context",
    ],

    // Properties metadata (nested metadata)
    propertiesMetadata: [
        "properties.metadata.createdBy",
        "properties.metadata.createdOn",
        "properties.metadata.updatedBy",
        "properties.metadata.updatedOn",
    ],

    // System data metadata
    systemDataMetadata: [
        "systemData.createdBy",
        "systemData.createdAt",
        "systemData.lastModifiedBy",
        "systemData.lastModifiedAt",
    ],

    // Conditional Access exclusions (environment-specific)
    conditionalAccessExclusions: [
        // User targeting (tenant-specific)
        "conditions.users.includeUsers",
        "conditions.users.excludeUsers",
        "conditions.users.includeGroups",
        "conditions.users.excludeGroups",

        // Location targeting (tenant-specific)
        "conditions.locations.includeLocations",
        "conditions.locations.excludeLocations",
    ],

    reviewers: [
        "reviewers",
        "reviewers[0].query",
        "reviewers[1].query",
        "reviewers[2].query",
        "reviewers[3].query",
        "reviewers[4].query",
    ],

    // URLs and references
    urlsAndReferences: [
        "appStoreUrl",
        "informationUrl",
        "SharePointUrl",
    ],

    // Device and deployment
    deviceAndDeployment: [
        "deviceNameTemplate",
        "deployedAppCount",
        "isAssigned",
    ],

    // Visual assets
    visualAssets: [
        "Size",
        "largeIcon.value",
    ],

    // Policies and settings
    policiesAndSettings: [
        "DefaultAuthenticationPolicy",
        "GroupLifeCyclePolicy",
        "GroupsCreationWhitelistedId",
    ],

    // Security and protection
    securitySettings: [
        "advancedThreatProtectionOnboardingBlob",
    ],

    // Account and identity
    accountIdentity: [
        "mailNickname",
        "onPremisesObjectIdentifier",
        "wellKnownObject",
    ],

    // Application metadata
    applicationMetadata: [
        "appMetadata",
        "description",
    ],
};

/**
 * Build excluded properties list from categories
 * @param {string|string[]} categories - 'all' or array of category names
 * @returns {string[]} Array of property names to exclude
 */
export const buildExcludedProperties = (categories = 'all') => {
    if (categories === 'all') {
        return Object.values(EXCLUDED_PROPERTY_CATEGORIES).flat();
    }

    if (typeof categories === 'string') {
        return EXCLUDED_PROPERTY_CATEGORIES[categories] || [];
    }

    return categories
        .map(cat => EXCLUDED_PROPERTY_CATEGORIES[cat] || [])
        .flat();
};

/**
 * Default excluded properties (all categories)
 */
export const DEFAULT_EXCLUDED_PROPERTIES = buildExcludedProperties('all');

/**
 * Merge custom excluded properties with defaults
 * @param {string[]} customProperties - Additional properties to exclude
 * @returns {string[]} Merged array with no duplicates
 */
export const mergeExcludedProperties = (customProperties = []) => {
    return [...new Set([...DEFAULT_EXCLUDED_PROPERTIES, ...customProperties])];
};

/**
 * Get excluded properties for a specific use case
 * @param {string} useCase - 'conditionalAccess', 'exchange', 'intune', etc.
 * @returns {string[]} Relevant excluded properties
 */
export const getExcludedPropertiesForUseCase = (useCase) => {
    const useCaseMappings = {
        conditionalAccess: [
            'metadata',
            'identifiers',
            'systemGenerated',
            'odataMetadata',
            'propertiesMetadata',
            'systemDataMetadata',
            'conditionalAccessExclusions',
            'authenticationTokens',
        ],
        exchange: [
            'metadata',
            'identifiers',
            'systemGenerated',
            'exchangeSpecific',
            'odataMetadata',
            'propertiesMetadata',
            'systemDataMetadata',
        ],
        intune: [
            'metadata',
            'identifiers',
            'systemGenerated',
            'deviceAndDeployment',
            'odataMetadata',
            'propertiesMetadata',
            'systemDataMetadata',
            'reviewers',
        ],
        azureAD: [
            'metadata',
            'identifiers',
            'systemGenerated',
            'accountIdentity',
            'odataMetadata',
            'propertiesMetadata',
            'systemDataMetadata',
            'conditionalAccessExclusions',
            'authenticationTokens',
            'reviewers',
        ],
    };

    const categories = useCaseMappings[useCase] || ['metadata', 'identifiers'];
    return buildExcludedProperties(categories);
};